/**
 * Monitor user activity within the application
 */

import { debugLog } from './debugLogger';

// Define activity events to monitor
const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keypress',
  'scroll',
  'touchstart',
  'click'
];

// Implementation details
let lastActiveTime = Date.now();
let activityCallbacks: ((timestamp: number) => void)[] = [];
let inactivityTimer: number | null = null;
const INACTIVITY_CHECK_INTERVAL = 60000; // Check every minute

/**
 * Records user activity
 */
const recordActivity = () => {
  const now = Date.now();
  
  // Only update if significant time has passed (avoid excessive updates)
  if (now - lastActiveTime > 1000) {
    lastActiveTime = now;
    
    // Call all registered callbacks
    activityCallbacks.forEach(callback => {
      try {
        callback(now);
      } catch (error) {
        console.error('Error in activity callback:', error);
      }
    });
  }
};

/**
 * Check for inactivity
 */
const checkInactivity = (threshold: number) => {
  const now = Date.now();
  const timeSinceLastActivity = now - lastActiveTime;
  
  if (timeSinceLastActivity >= threshold) {
    debugLog(`User inactive for ${Math.floor(timeSinceLastActivity / 1000)}s`);
    return true;
  }
  
  return false;
};

/**
 * Initialize the activity monitor
 */
export const initActivityMonitor = () => {
  if (typeof window === 'undefined') return () => {};
  
  // Add listeners for all activity events
  ACTIVITY_EVENTS.forEach(eventName => {
    window.addEventListener(eventName, recordActivity, { passive: true });
  });
  
  // Initial activity record
  recordActivity();
  
  // Return cleanup function
  return () => {
    ACTIVITY_EVENTS.forEach(eventName => {
      window.removeEventListener(eventName, recordActivity);
    });
    
    if (inactivityTimer !== null) {
      clearInterval(inactivityTimer);
    }
  };
};

/**
 * Register a callback for activity events
 * @param callback Function to call when activity is recorded
 * @returns Function to remove the callback
 */
export const onActivity = (callback: (timestamp: number) => void) => {
  activityCallbacks.push(callback);
  
  return () => {
    activityCallbacks = activityCallbacks.filter(cb => cb !== callback);
  };
};

/**
 * Get the last time the user was active
 */
export const getLastActiveTime = () => {
  return lastActiveTime;
};

/**
 * Start monitoring for inactivity
 * @param threshold Time in ms before user is considered inactive
 * @param callback Function to call when user becomes inactive
 */
export const startInactivityMonitor = (threshold: number, callback: () => void) => {
  if (inactivityTimer !== null) {
    clearInterval(inactivityTimer);
  }
  
  inactivityTimer = window.setInterval(() => {
    if (checkInactivity(threshold)) {
      callback();
    }
  }, INACTIVITY_CHECK_INTERVAL);
  
  return () => {
    if (inactivityTimer !== null) {
      clearInterval(inactivityTimer);
      inactivityTimer = null;
    }
  };
};
